import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '../../utils/api';
import {
  hasAnyPermissionForModule,
  hasAssetsInventoryModuleAccess,
  hasPeopleModuleAccess,
  canFeatureForRole,
  getEffectiveMatrixRoleKey,
  PERMISSIONS,
} from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import '../../styles/Dashboard.css';

/** Employee app routes only include Teams, Matches, and personal Assets views. */
const MODULE_TILES_EMPLOYEE = [
  { module: 'Teams', label: 'Teams', icon: '🎉', path: '/employee/teams-events' },
  { module: 'Matches', label: 'Matches', icon: '🎯', path: '/employee/matches' },
  { tileId: 'my-assets', module: 'My Assets', label: 'My Assets', icon: '📦', path: '/employee/my-assets' },
  { tileId: 'assets-module', module: 'Assets', label: 'Assets', icon: '📦', path: '/employee/assets' },
  {
    tileId: 'asset-handover',
    module: 'Asset Handover',
    label: 'My Asset Handover',
    icon: '📋',
    path: '/employee/my-asset-handover',
  },
  {
    tileId: 'people-module',
    module: 'People',
    label: 'People',
    icon: '👥',
    path: '/employee/people',
  },
  {
    tileId: 'reports-module',
    module: 'Reports',
    label: 'Reports',
    icon: '📈',
    path: '/employee/reports',
  },
];

const EmployeeDashboardPage = () => {
  const navigate = useNavigate();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const { loading: referenceDataLoading } = useReferenceData();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    myAssets: 0,
    pendingAcknowledgements: 0,
    acknowledgedAssets: 0,
    totalValue: 0
  });
  const [recentAssets, setRecentAssets] = useState([]);
  const [pendingHandovers, setPendingHandovers] = useState([]);
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const normalizedUserRole = getEffectiveMatrixRoleKey(user);

  const canViewAssignedAssets = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_ASSIGNED_ASSETS,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  const canViewMyAssetHandover = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  const hasAssetModuleAccess = useMemo(
    () => hasAssetsInventoryModuleAccess(normalizedUserRole, rolePermissions),
    [normalizedUserRole, rolePermissions]
  );

  const hasPeopleAccess = useMemo(
    () => hasPeopleModuleAccess(normalizedUserRole, rolePermissions),
    [normalizedUserRole, rolePermissions]
  );

  const hasReportsAccess = useMemo(
    () => hasAnyPermissionForModule(normalizedUserRole, rolePermissions, 'Reports'),
    [normalizedUserRole, rolePermissions]
  );

  const moduleTileAllowed = (tile) => {
    if (tile.module === 'Teams' || tile.module === 'Matches') {
      if (!permissionsReady || referenceDataLoading) return false;
    }
    if (tile.tileId === 'my-assets') return canViewAssignedAssets;
    if (tile.tileId === 'assets-module') return hasAssetModuleAccess;
    if (tile.tileId === 'asset-handover') return canViewMyAssetHandover;
    if (tile.tileId === 'people-module') return hasPeopleAccess;
    if (tile.tileId === 'reports-module') return hasReportsAccess;
    return hasAnyPermissionForModule(normalizedUserRole, rolePermissions, tile.module);
  };

  useEffect(() => {
    if (!permissionsReady) return;
    fetchDashboardData();
  }, [permissionsReady, canViewAssignedAssets, canViewMyAssetHandover]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const userId = user.id || user._id;

      let myAssets = 0;
      let acknowledgedAssets = 0;
      let totalValue = 0;
      let assets = [];

      if (canViewAssignedAssets && userId) {
        try {
          const assetsResponse = await api.getAssets({ assignedTo: userId, limit: 10000 });
          assets = assetsResponse.data || [];
          const allMyAssets = assetsResponse.data || [];
          const acknowledgedAssetsList = allMyAssets.filter(
            (a) =>
              (a.status === 'received' || a.status === 'issue_reported') &&
              a.status !== 'in_transfer'
          );
          acknowledgedAssets = acknowledgedAssetsList.length;
          myAssets = assetsResponse.total !== undefined ? assetsResponse.total : assets.length;
          totalValue = assets.reduce((sum, asset) => sum + (parseFloat(asset.cost) || 0), 0);
          setRecentAssets(assets.slice(0, 5));
        } catch {
          setRecentAssets([]);
        }
      } else {
        setRecentAssets([]);
      }

      let pendingAcknowledgements = 0;
      if (canViewMyAssetHandover) {
        try {
          const handoversResponse = await api.getHandovers({ limit: 10000 });
          const handovers = handoversResponse.data || [];
          const pendingHandoversList = handovers.filter((h) => h.acknowledgementStatus === 'pending');
          setPendingHandovers(pendingHandoversList.slice(0, 5));

          const pendingResponse = await api.getHandovers({
            acknowledgementStatus: 'pending',
            pendingFilter: 'pending_only',
            limit: 1,
          });
          pendingAcknowledgements =
            pendingResponse.total !== undefined ? pendingResponse.total : pendingHandoversList.length;
        } catch {
          setPendingHandovers([]);
        }
      } else {
        setPendingHandovers([]);
      }

      setStats({
        myAssets,
        pendingAcknowledgements,
        acknowledgedAssets,
        totalValue,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      acknowledged: '#10b981',
      issue_reported: '#ef4444',
      available: '#10b981',
      assigned: '#3b82f6',
      maintenance: '#f59e0b'
    };
    return colors[status] || '#6b7280';
  };

  if (loading || !permissionsReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Same four summary cards for every non-admin role; counts stay 0 when User Roles block the API */}
      <div className="stats-grid">
        <div
          className="stat-card stat-primary"
          style={{ opacity: canViewAssignedAssets ? 1 : 0.75 }}
        >
          <div className="stat-icon-wrapper">
            <div className="stat-icon">📦</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{canViewAssignedAssets ? stats.myAssets : 0}</h3>
            <p className="stat-label">My Assets</p>
            <div className="stat-trend">
              <span className="trend-up">Assigned to me</span>
            </div>
          </div>
        </div>

        <div
          className="stat-card stat-warning"
          style={{ opacity: canViewMyAssetHandover ? 1 : 0.75 }}
        >
          <div className="stat-icon-wrapper">
            <div className="stat-icon">⚠️</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">
              {canViewMyAssetHandover ? stats.pendingAcknowledgements : 0}
            </h3>
            <p className="stat-label">Pending Acknowledgements</p>
            <div className="stat-trend">
              <span className="trend-warning">Action required</span>
            </div>
          </div>
        </div>

        <div
          className="stat-card stat-success"
          style={{ opacity: canViewAssignedAssets ? 1 : 0.75 }}
        >
          <div className="stat-icon-wrapper">
            <div className="stat-icon">✅</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">
              {canViewAssignedAssets ? stats.acknowledgedAssets : 0}
            </h3>
            <p className="stat-label">Acknowledged Assets</p>
            <div className="stat-trend">
              <span className="trend-up">Confirmed received</span>
            </div>
          </div>
        </div>

        <div
          className="stat-card stat-purple"
          style={{ opacity: canViewAssignedAssets ? 1 : 0.75 }}
        >
          <div className="stat-icon-wrapper">
            <div className="stat-icon">💰</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">
              {canViewAssignedAssets ? formatCurrency(stats.totalValue) : formatCurrency(0)}
            </h3>
            <p className="stat-label">Total Asset Value</p>
            <div className="stat-trend">
              <span className="trend-up">Under my care</span>
            </div>
          </div>
        </div>
      </div>

      {/* {permissionsReady && (
        <div className="dashboard-card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h2 className="card-title">Modules</h2>
          </div>
          <div className="card-body">
            <div className="quick-actions-grid">
              {MODULE_TILES_EMPLOYEE.map((t) => {
                const allowed = moduleTileAllowed(t);
                return (
                  <button
                    key={t.tileId || t.module}
                    type="button"
                    className="quick-action-btn"
                    disabled={!allowed}
                    title={!allowed ? 'Not enabled for your role in Settings → User Roles' : undefined}
                    style={!allowed ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                    onClick={() => {
                      if (!allowed) return;
                      navigate(t.path);
                    }}
                  >
                    <div className="quick-action-icon">{t.icon}</div>
                    <div className="quick-action-label">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )} */}

      {/* Pending Handovers Alert */}
      {canViewMyAssetHandover && stats.pendingAcknowledgements > 0 && (
        <div className="dashboard-card" style={{ 
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderLeft: '4px solid #f59e0b',
          marginBottom: '24px'
        }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '32px' }}>🔔</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#92400e' }}>
                  Action Required
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#78350f' }}>
                  You have {stats.pendingAcknowledgements} asset{stats.pendingAcknowledgements !== 1 ? 's' : ''} waiting for acknowledgement.
                </p>
              </div>
              <button 
                className="btn-primary"
                onClick={() => navigate('/employee/my-asset-handover')}
                style={{ flexShrink: 0 }}
              >
                Review Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Same two-column row for every non-admin role */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Pending Acknowledgements</h2>
            <button
              type="button"
              className="btn-link"
              disabled={!canViewMyAssetHandover}
              style={!canViewMyAssetHandover ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
              onClick={() => navigate('/employee/my-asset-handover')}
            >
              View All
            </button>
          </div>
          <div className="card-body">
            {!canViewMyAssetHandover ? (
              <div className="empty-state">
                <p>Handover list is not enabled for your role in Settings → User Roles.</p>
              </div>
            ) : pendingHandovers.length > 0 ? (
              <div className="recent-list">
                {pendingHandovers.map((handover) => (
                  <div
                    key={handover._id || handover.id}
                    className="recent-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/employee/my-asset-handover')}
                  >
                    <div className="recent-item-icon">
                      <div
                        className="status-dot"
                        style={{ backgroundColor: getStatusColor(handover.acknowledgementStatus) }}
                      ></div>
                    </div>
                    <div className="recent-item-content">
                      <div className="recent-item-title">
                        {handover.asset?.assetTag || 'N/A'} - {handover.asset?.description || 'Asset'}
                      </div>
                      <div className="recent-item-meta">
                        Assigned on: {formatDate(handover.assignedDate)} • By:{' '}
                        {handover.assignedBy?.name || 'Admin'}
                      </div>
                    </div>
                    <div className="recent-item-status">
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(handover.acknowledgementStatus) }}
                      >
                        Pending
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No pending acknowledgements</p>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">My Recent Assets</h2>
            <button
              type="button"
              className="btn-link"
              disabled={!canViewAssignedAssets}
              style={!canViewAssignedAssets ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
              onClick={() => navigate('/employee/my-assets')}
            >
              View All
            </button>
          </div>
          <div className="card-body">
            {!canViewAssignedAssets ? (
              <div className="empty-state">
                <p>Assigned assets are not enabled for your role in Settings → User Roles.</p>
              </div>
            ) : recentAssets.length > 0 ? (
              <div className="recent-list">
                {recentAssets.map((asset) => (
                  <div
                    key={asset._id || asset.id}
                    className="recent-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/employee/assets/${asset._id || asset.id}`)}
                  >
                    <div className="recent-item-icon">
                      {asset.photos && asset.photos.length > 0 && asset.photos[0].url ? (
                        <img
                          src={getImageUrl(asset.photos[0].url)}
                          alt={asset.assetTag}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: 600,
                          }}
                        >
                          {asset.assetTag?.charAt(0) || 'A'}
                        </div>
                      )}
                    </div>
                    <div className="recent-item-content">
                      <div className="recent-item-title">
                        {asset.assetTag} - {asset.description}
                      </div>
                      <div className="recent-item-meta">
                        {asset.brand} {asset.model} • {asset.location?.site?.name || 'N/A'}
                      </div>
                    </div>
                    <div className="recent-item-status">
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(asset.status) }}
                      >
                        {asset.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No assets assigned yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2 className="card-title">Quick Actions</h2>
        </div>
        <div className="card-body">
          <div className="quick-actions-grid">
            <button
              type="button"
              className="quick-action-btn"
              disabled={!hasAssetModuleAccess}
              style={!hasAssetModuleAccess ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              title={!hasAssetModuleAccess ? 'Not enabled for your role in Settings → User Roles' : undefined}
              onClick={() => hasAssetModuleAccess && navigate('/employee/assets')}
            >
              <div className="quick-action-icon">📦</div>
              <div className="quick-action-label">Assets</div>
            </button>
            <button
              type="button"
              className="quick-action-btn"
              disabled={!canViewMyAssetHandover}
              style={!canViewMyAssetHandover ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              title={!canViewMyAssetHandover ? 'Not enabled for your role in Settings → User Roles' : undefined}
              onClick={() => canViewMyAssetHandover && navigate('/employee/my-asset-handover')}
            >
              <div className="quick-action-icon">📋</div>
              <div className="quick-action-label">My Handovers</div>
            </button>
            <button
              type="button"
              className="quick-action-btn"
              disabled={!canViewAssignedAssets}
              style={!canViewAssignedAssets ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              title={!canViewAssignedAssets ? 'Not enabled for your role in Settings → User Roles' : undefined}
              onClick={() => canViewAssignedAssets && navigate('/employee/my-assets')}
            >
              <div className="quick-action-icon">📦</div>
              <div className="quick-action-label">My Assets</div>
            </button>
            <button
              type="button"
              className="quick-action-btn"
              onClick={() => navigate('/employee/profile')}
            >
              <div className="quick-action-icon">👤</div>
              <div className="quick-action-label">My Profile</div>
            </button>
            <button
              type="button"
              className="quick-action-btn"
              onClick={() => navigate('/employee/notifications')}
            >
              <div className="quick-action-icon">🔔</div>
              <div className="quick-action-label">Notifications</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboardPage;
